// Fact Blaster: asteroids carry answers; tap the right one and the ship fires.
// Asteroids that reach the bottom loop back to the top. Nothing ever ends the round early.

import { h, clear } from '@kit/dom';
import { makeSurface } from '@kit/canvas';
import { attachPointer } from '@kit/pointer';
import { makeLoop } from '@kit/loop';
import type { GameModule, GameContext } from '@shell/types';
import { makeFact, type Fact } from '@problems/facts';

const FACTS = 12;

interface Asteroid { label: string; x: number; y: number; r: number; vy: number; spin: number; rot: number; flash: number; dead: number }
interface Spark { x: number; y: number; vx: number; vy: number; life: number; color: string }

function mount(host: HTMLElement, ctx: GameContext) {
  let n = 0;
  let fact!: Fact;
  let rocks: Asteroid[] = [];
  let sparks: Spark[] = [];
  let laser: { x: number; y: number; t: number } | null = null;
  let misses = 0, hinted = false;
  let attempts = 0, correct = 0;
  let level = ctx.currentLevel();
  let maxLevel = level;
  let locked = false;
  const t0 = performance.now();
  let tProblem = performance.now();

  const head = h('div.row', { style: { justifyContent: 'space-between' } }, h('span.muted', null, 'Fact ', h('strong', null, '1'), ` of ${FACTS}`), h('span.muted.small', null, 'Tap the right asteroid'));
  const prompt = h('div.prompt', { style: { fontSize: '34px' } });
  const stage = h('div');
  const help = h('p.center.muted.small', null, 'The ship fires at whatever you tap.');
  host.append(head, prompt, stage, help);

  const S = makeSurface(stage, { aspect: 1.1, onResize: () => place() });

  function speedFor(l: number): number { return 14 + l * 4; }   // px per second

  function newFact() {
    level = ctx.currentLevel();
    maxLevel = Math.max(maxLevel, level);
    fact = makeFact(level, ctx.rng);
    misses = 0; hinted = false; locked = false;
    tProblem = performance.now();
    (head.querySelector('strong') as HTMLElement).textContent = String(n + 1);
    prompt.textContent = `${fact.text} = ?`;
    help.textContent = 'The ship fires at whatever you tap.';
    place();
  }

  function place() {
    if (!fact || !S.w) return;
    const W = S.w, H = S.h;
    const r = Math.max(24, Math.min(W / 8, 34));
    const lanes = fact.choices.length;
    rocks = fact.choices.map((label, i) => ({
      label,
      x: W * ((i + 0.5) / lanes) + (ctx.rng() - 0.5) * r * 0.6,
      y: -r - ctx.rng() * H * 0.6,
      r: r + (label.length > 3 ? 4 : 0),
      vy: speedFor(level) * (0.8 + ctx.rng() * 0.5),
      spin: (ctx.rng() - 0.5) * 1.2,
      rot: ctx.rng() * 6.28,
      flash: 0,
      dead: 0,
    }));
  }

  function update(dt: number) {
    const H = S.h;
    for (const a of rocks) {
      if (a.dead > 0) { a.dead = Math.max(0, a.dead - dt * 2.5); continue; }
      a.y += a.vy * dt;
      a.rot += a.spin * dt;
      if (a.flash > 0) a.flash = Math.max(0, a.flash - dt * 3);
      if (a.y > H * 0.72) { a.y = -a.r; }   // loop back up before reaching the ship
    }
    for (const s of sparks) { s.x += s.vx * dt; s.y += s.vy * dt; s.vy += 200 * dt; s.life -= dt; }
    sparks = sparks.filter(s => s.life > 0);
    if (laser) { laser.t -= dt; if (laser.t <= 0) laser = null; }
  }

  function draw() {
    const c = S.ctx, W = S.w, H = S.h;
    c.fillStyle = '#1b1f3a'; c.fillRect(0, 0, W, H);
    // stars
    c.fillStyle = 'rgba(255,255,255,.7)';
    for (let i = 0; i < 40; i++) { const sx = (i * 73) % W, sy = (i * 131 + performance.now() * 0.01) % H; c.fillRect(sx, sy, 2, 2); }
    // asteroids
    for (const a of rocks) {
      if (a.dead > 0 && a.dead < 0.01) continue;
      c.save();
      c.translate(a.x, a.y);
      c.rotate(a.rot);
      const scale = a.dead > 0 ? a.dead : 1;
      c.globalAlpha = a.dead > 0 ? a.dead : 1;
      c.fillStyle = a.flash > 0 ? '#e2574c' : '#8a8576';
      c.beginPath();
      for (let i = 0; i < 8; i++) { const ang = i * Math.PI / 4; const rr = a.r * scale * (i % 2 ? 0.86 : 1); c.lineTo(Math.cos(ang) * rr, Math.sin(ang) * rr); }
      c.closePath(); c.fill();
      c.fillStyle = 'rgba(0,0,0,.18)'; c.beginPath(); c.arc(-a.r * 0.3, a.r * 0.2, a.r * 0.18, 0, 7); c.arc(a.r * 0.35, -a.r * 0.3, a.r * 0.12, 0, 7); c.fill();
      c.rotate(-a.rot);
      c.fillStyle = '#fff';
      c.font = `900 ${Math.round(a.r * (a.label.length > 4 ? 0.5 : a.label.length > 2 ? 0.62 : 0.85))}px Nunito, sans-serif`;
      c.textAlign = 'center'; c.textBaseline = 'middle';
      c.fillText(a.label, 0, 1);
      c.restore();
    }
    // ship
    const sx = W / 2, sy = H * 0.9;
    c.fillStyle = '#3bb3a9';
    c.beginPath(); c.moveTo(sx, sy - 26); c.lineTo(sx + 20, sy + 10); c.lineTo(sx - 20, sy + 10); c.closePath(); c.fill();
    c.fillStyle = '#bfe8ff'; c.beginPath(); c.arc(sx, sy - 6, 6, 0, 7); c.fill();
    c.fillStyle = '#f08a3c'; c.beginPath(); c.moveTo(sx - 8, sy + 10); c.lineTo(sx, sy + 22 + Math.sin(performance.now() / 60) * 4); c.lineTo(sx + 8, sy + 10); c.closePath(); c.fill();
    // laser
    if (laser) {
      c.strokeStyle = '#f5c542'; c.lineWidth = 3; c.globalAlpha = Math.min(1, laser.t * 4);
      c.beginPath(); c.moveTo(sx, sy - 26); c.lineTo(laser.x, laser.y); c.stroke();
      c.globalAlpha = 1;
    }
    for (const s of sparks) { c.fillStyle = s.color; c.globalAlpha = Math.max(0, s.life); c.fillRect(s.x, s.y, 4, 4); }
    c.globalAlpha = 1;
  }

  const loop = makeLoop(update, draw);
  loop.start();

  const unPointer = attachPointer(S.canvas, {
    tap(p) {
      if (locked) return;
      let hit: Asteroid | null = null;
      for (const a of rocks) if (a.dead === 0 && Math.hypot(p.x - a.x, p.y - a.y) <= a.r + 10) { hit = a; break; }
      if (!hit) return;
      laser = { x: hit.x, y: hit.y, t: 0.25 };
      const ms = performance.now() - tProblem;
      if (hit.label === fact.answer) {
        locked = true;
        attempts++; correct++;
        ctx.report({ skill: fact.skill, level, correct: true, hintUsed: hinted, ms, problemId: `fact:${fact.text}` });
        ctx.sound('ding');
        hit.dead = 1;
        for (let i = 0; i < 14; i++) sparks.push({ x: hit.x, y: hit.y, vx: (ctx.rng() - 0.5) * 220, vy: (ctx.rng() - 0.8) * 220, life: 0.6 + ctx.rng() * 0.4, color: ['#f5c542', '#f08a3c', '#fff'][i % 3] });
        ctx.say(`${fact.text} = ${fact.answer}. Blasted!`);
        n++;
        if (n >= FACTS) {
          if (attempts === correct && maxLevel >= 5) ctx.charm('charm.blaster12');
          setTimeout(() => ctx.end({ attempts, correct, perfect: attempts === correct, durationMs: performance.now() - t0 }), 800);
          return;
        }
        setTimeout(newFact, 700);
      } else {
        misses++;
        if (misses === 1) { attempts++; ctx.report({ skill: fact.skill, level, correct: false, hintUsed: hinted, ms, problemId: `fact:${fact.text}` }); }
        ctx.sound('buzz');
        hit.flash = 1;
        hinted = true;
        help.textContent = `Hint: ${fact.hint}`;
        ctx.say(`Not ${hit.label}. Look again.`);
      }
    },
  });

  newFact();
  return { destroy() { loop.stop(); unPointer(); S.destroy(); clear(host); } };
}

const mod: GameModule = {
  id: 'math.fact-blaster',
  title: 'Fact Blaster',
  blurb: 'Asteroids carry answers. Tap the right one and the ship fires. Fast facts, no pressure.',
  category: 'math',
  skills: ['mult_div', 'add_sub'],
  levelRange: [1, 10],
  unlock: { stars: 9 },
  mount,
};
export default mod;
